import React, { FC, useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { Loader } from "dash-styled"

import {
    AppDetailBase,
    AppDetailBaseProps
} from "../AppDetailBase/AppDetailBase"
import {
    hasValue,
    FetchState
} from "../../../../../../Models/Client/FetchState"
import { updateAppTTLSettings } from "../../../../../../Actions/API/AppInstance/AppInstanceActions"
import { Result } from "../../../../../../Models/Utility/Result"
import { AppTTLSettings } from "../../../../../../Models/API/AppSettings"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { dirtinessChanged } from "../../../../../../Actions/Client/InputRender/InputRenderActions"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { SettingsForm } from "./SettingsForm"

interface GeneralSettingsProps {
    orgUsername: string
    appID: string
}

export interface FormFieldsTTL {
    ttlSettings: AppTTLSettings
    cancelAndRelabelJobsInState: string[]
}

export const GeneralSettings: FC<GeneralSettingsProps> = ({
    orgUsername,
    appID
}) => {
    const dispatch = useDispatch()
    const instanceFetch = useAppInstance(appID)
    const [userExpiryContact, , hideUserExpiryContact] = useBoolean(false)

    const baseProps: AppDetailBaseProps = {
        orgUsername,
        id: appID,
        title: "General Settings",
        breadcrumbs: [],
        fixedContentWidth: false,
        forceUserExpiryContact: userExpiryContact,
        resetUserExpiryContact: hideUserExpiryContact
    }

    const formFieldsFetch = useMemo<FetchState<FormFieldsTTL>>(
        () =>
            !hasValue(instanceFetch)
                ? instanceFetch
                : Result.ok({
                      cancelAndRelabelJobsInState: [],
                      ttlSettings: instanceFetch.value.settings.ttlSettings
                  }),
        [instanceFetch]
    )

    const handleSaveChanges = useCallback(
        async (values: FormFieldsTTL) => {
            await dispatch(
                updateAppTTLSettings(
                    appID,
                    values.cancelAndRelabelJobsInState,
                    values.ttlSettings
                )
            )
        },
        [appID, dispatch]
    )
    const handleDirtinessChanged = useCallback(
        (isDirty: boolean) => dispatch(dirtinessChanged(isDirty)),
        [dispatch]
    )

    if (!hasValue(formFieldsFetch)) {
        return (
            <AppDetailBase {...baseProps}>
                <Loader />
            </AppDetailBase>
        )
    }

    return (
        <AppDetailBase {...baseProps}>
            <SettingsForm
                appID={appID}
                initialValue={formFieldsFetch.value}
                onSave={handleSaveChanges}
                onDirtinessChanged={handleDirtinessChanged}
            />
        </AppDetailBase>
    )
}
